import * as React from 'react';

import { Typography } from '@nelnet/unifi-components-react';

import { IPageDataItems } from '../../utils/declarations';
import usePageData from '../../hooks/usePageData';
import PrimaryLayout from '../../../layouts/PrimaryLayout';
import PageIntro from '../PageIntro';
import PlatformCards from '../../modules/PlatformCards';
import Link from '../../modules/Link';
import PlatformSubnav from '../LayoutFrame/PlatformSubnav';
import usePlatformVersion from '../../hooks/usePlatformVersion';

interface IPlatformPageProps {
  title: string;
  slug: string;
  intro: string;
  platform: string;
}

const PlatformPage: React.FunctionComponent<IPlatformPageProps> = ({
  title,
  slug,
  intro,
  platform,
}) => {
  const children: IPageDataItems[] = usePageData({ type: 'tree', slug, returnChildren: true });
  const version = usePlatformVersion(platform);

  return (
    <PrimaryLayout title={title} path={slug}>
      <PlatformSubnav path={slug} />
      <PageIntro title={title} intro={intro} path={slug} version={version} />
      <div>
        <Typography>
          A component is a UI element that can be used more than twice in the same way and can be
          broken down to its basics. It can have variants or variations.
        </Typography>
        <PlatformCards cardItems={children} />
        <Typography variant="h2" gutterBottom margin={{ top: 4 }}>
          Using Components
        </Typography>
        <Typography paragraph>
          The <Link href={`/getstarted/${platform}/`}>Get Started</Link> page has instructions on
          how to install the Unifi components. Each component page has interactive examples with
          linked code of the components use cases.
        </Typography>
        <Typography variant="h2" gutterBottom>
          Component Methodology
        </Typography>
        <Typography paragraph>
          Components should be flexible enough to meet diverse needs. The components are set up to
          be restructured based on the information passed in. No matter what type of experience
          you’re creating, you can use components as the building blocks of your product or feature.
        </Typography>
        <Typography paragraph>
          Each of the components has a well-documented public interface (API) with guidelines and
          well-defined conventions. This way, developers don’t need to worry about the underlying
          implementation. Instead, they can focus on creating great user experiences.
        </Typography>
        <Typography variant="h2" gutterBottom>
          Feedback
        </Typography>
        <Typography paragraph>
          We are constantly working to improve Unifi so that it better serves users. Please{' '}
          <Link href="/contact/">submit your issues or feature requests</Link> to the Unifi team.
        </Typography>
      </div>
    </PrimaryLayout>
  );
};

export default PlatformPage;
